import React, { useRef, useState, useEffect } from "react";
import { Navigate, NavLink, useNavigate } from "react-router-dom";

import {
  FiInbox,
  FiFileText,
  FiSend,
  FiStar,
  FiChevronLeft,
  FiChevronRight,
  FiSettings,
  FiUser,
  FiHelpCircle,
} from "react-icons/fi";

import { LuPencil, LuLayoutDashboard, LuLogOut } from "react-icons/lu";
import { BsArchive } from "react-icons/bs";
import { FaRegTrashCan } from "react-icons/fa6";
import { useClickContext } from "src/context/ClickContext";
import { useNotify } from "src/context/NotifyContext";
import { PiGameControllerDuotone } from "react-icons/pi";
import { FaUsersGear } from "react-icons/fa6";
import logoWithName from "src/assets/images/crypsyn-full-logo.png";
import logo from "src/assets/images/crypsyn-logo.png";
import { MdEdit, MdPayment } from "react-icons/md";

import { useSocketContext } from "src/context/SocketContext";
import { IoNotificationsOutline } from "react-icons/io5";

export default function Sidebar({ isSidebarOpen, setIsSidebarOpen }) {
  const [collapsed, setCollapsed] = useState(false);

  const userdetails = localStorage.getItem("userDetails");
  const userdata = userdetails ? JSON.parse(userdetails) : {};
  const role = userdata?.role;

  const { setNotify } = useNotify();
  const { open } = useClickContext();

  const navigate = useNavigate();
  const { disconnect } = useSocketContext();

  const toggle = () => {
    setCollapsed((s) => !s);
    setNotify(collapsed ? "expanded" : "collapsed");
  };

  const handleMobileMenuClose = () => {
    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  };

  const handleMobileSidebarClose = () => {
    open();

    if (window.innerWidth < 768) {
      setIsSidebarOpen(false);
    }
  };

  const sidebarRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        window.innerWidth < 768 &&
        sidebarRef.current &&
        !sidebarRef.current.contains(event.target)
      ) {
        setIsSidebarOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    localStorage.clear();

    disconnect();
    navigate("/");
  };

  const navItemClass = ({ isActive }) =>
    `
      group relative flex items-center
      ${collapsed ? "justify-center" : "gap-3"}
      w-full
      px-3
      py-2.5
      rounded-xl
      text-sm
      font-medium
      transition-all
      duration-200
      ease-out
      ${
        isActive
          ? "bg-[#edf4ff] text-[#2f66ad]"
          : "text-[#536174] hover:bg-[#f6f9fd] hover:text-[#2f66ad]"
      }
    `;

  const mobileNavItemClass = ({ isActive }) =>
    `
      flex items-center gap-3
      w-full
      px-3
      py-2.5
      rounded-xl
      text-sm
      font-medium
      transition-all duration-200
      ${
        isActive
          ? "bg-[#edf4ff] text-[#2f66ad]"
          : "text-[#536174] hover:bg-[#f6f9fd] hover:text-[#2f66ad]"
      }
    `;

  const iconClass = (isActive) =>
    `
      flex-shrink-0
      text-[18px]
      transition-all
      duration-200
      ${
        isActive
          ? "text-[#2f66ad]"
          : "text-[#7b8798] group-hover:text-[#2f66ad]"
      }
    `;

  return (
    <>
      {/* Mobile Overlay */}
      {isSidebarOpen && (
        <div
          className="
            fixed inset-0
            bg-slate-900/10
            backdrop-blur-[2px]
            z-40
            md:hidden
          "
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      <aside
        ref={sidebarRef}
        className={`
          fixed md:static
          left-0 top-0
          z-50
          h-screen
          flex flex-col
          bg-white
          text-[#253247]
          border-r border-[#e8edf4]
          shadow-[4px_0_24px_rgba(30,64,100,0.04)]
          transition-all
          duration-300
          ease-in-out

          ${
            isSidebarOpen
              ? "translate-x-0"
              : "-translate-x-full md:translate-x-0"
          }

          ${collapsed ? "md:w-[76px]" : "md:w-[256px]"}
          w-[280px]
        `}
      >
        {/* ========================================================= */}
        {/* HEADER */}
        {/* ========================================================= */}

        <div
          className={`
            h-[72px]
            flex
            items-center
            border-b border-[#edf1f6]
            flex-shrink-0
            ${
              collapsed
                ? "justify-center px-3"
                : "justify-between px-4"
            }
          `}
        >
          <div
            className={`
              flex items-center
              ${collapsed ? "justify-center" : ""}
            `}
          >
            {!collapsed ? (
              <img
                src={logoWithName}
                alt="Crypsyn"
                className="
                  w-auto
                  h-[31px]
                  object-contain
                  max-w-[170px]
                "
              />
            ) : (
              <img
                src={logo}
                alt="Crypsyn"
                className="
                  w-[38px]
                  h-[38px]
                  object-contain
                "
              />
            )}
          </div>

          {/* Desktop Collapse */}
          <button
            onClick={toggle}
            className="
              hidden md:flex
              w-8 h-8
              items-center justify-center
              rounded-lg
              border border-[#e8edf4]
              bg-white
              text-[#718096]
              hover:text-[#2f66ad]
              hover:bg-[#f5f8fc]
              hover:border-[#dbe6f3]
              transition-all
              duration-200
            "
          >
            {collapsed ? (
              <FiChevronRight size={17} />
            ) : (
              <FiChevronLeft size={17} />
            )}
          </button>
        </div>

        {/* ========================================================= */}
        {/* COMPOSE */}
        {/* ========================================================= */}

        <div
          className={`
            px-3
            pt-5
            pb-3
            flex-shrink-0
            ${collapsed ? "flex justify-center" : ""}
          `}
        >
          <button
            onClick={handleMobileSidebarClose}
            title={collapsed ? "Compose" : ""}
            className={`
              relative
              overflow-hidden
              group
              ${
                collapsed
                  ? "w-11 h-11 rounded-xl"
                  : "w-full h-[46px] rounded-xl"
              }
              flex items-center justify-center
              gap-2
              text-white
              font-semibold
              bg-gradient-to-r
              from-[#326ab6]
              via-[#4f82c4]
              to-[#7da4d8]
              shadow-[0_8px_20px_rgba(58,112,184,0.22)]
              hover:shadow-[0_10px_25px_rgba(58,112,184,0.30)]
              hover:-translate-y-[1px]
              active:translate-y-0
              transition-all
              duration-200
            `}
          >
            {/* Shine */}
            <span
              className="
                absolute inset-0
                bg-gradient-to-r
                from-transparent
                via-white/15
                to-transparent
                -translate-x-full
                group-hover:translate-x-full
                transition-transform
                duration-700
              "
            />

            {!collapsed ? (
              <>
                <MdEdit size={18} />
                <span className="text-[13px] tracking-wide">
                  Compose
                </span>
              </>
            ) : (
              <LuPencil size={18} />
            )}
          </button>
        </div>

        {/* ========================================================= */}
        {/* NAVIGATION */}
        {/* ========================================================= */}

        <nav
          className="
            flex-1
            px-3
            pb-3
            overflow-y-auto
            overflow-x-hidden

            scrollbar-thin
            scrollbar-thumb-[#dce5f0]
            scrollbar-track-transparent
          "
        >
          {/* MAIN */}
          {!collapsed && (
            <div
              className="
                px-3
                pt-2
                pb-2
                text-[10px]
                font-bold
                tracking-[0.12em]
                uppercase
                text-[#9aa7b8]
              "
            >
              Main
            </div>
          )}

          <ul className="space-y-1">
            {/* ===================================================== */}
            {/* MOBILE PROFILE */}
            {/* ===================================================== */}

            <li className="md:hidden">
              <NavLink
                id="profile-menu-profile"
                to="/profile"
                onClick={handleMobileMenuClose}
                className={mobileNavItemClass}
              >
                {({ isActive }) => (
                  <>
                    <FiUser
                      size={18}
                      className={
                        isActive
                          ? "text-[#2f66ad]"
                          : "text-[#7b8798]"
                      }
                    />

                    <span>Profile</span>
                  </>
                )}
              </NavLink>
            </li>

            {/* ===================================================== */}
            {/* NOTIFICATIONS */}
            {/* ===================================================== */}

            {role === "admin" && (
              <li>
                <NavLink
                  to="/notifications"
                  onClick={handleMobileMenuClose}
                  className={
                    collapsed
                      ? navItemClass
                      : navItemClass
                  }
                >
                  {({ isActive }) => (
                    <>
                      <IoNotificationsOutline
                        size={19}
                        className={iconClass(isActive)}
                      />

                      {!collapsed && (
                        <span>Notifications</span>
                      )}
                    </>
                  )}
                </NavLink>
              </li>
            )}

            {/* ===================================================== */}
            {/* DASHBOARD */}
            {/* ===================================================== */}

            {role === "super_admin" && (
              <li>
                <NavLink
                  to={
                    role === "admin"
                      ? "/admin-dashboard"
                      : role === "super_admin"
                      ? "/dashboard"
                      : "/dashboard"
                  }
                  onClick={handleMobileMenuClose}
                  className={navItemClass}
                >
                  {({ isActive }) => (
                    <>
                      <LuLayoutDashboard
                        className={iconClass(isActive)}
                        size={18}
                      />

                      {!collapsed && (
                        <span>Dashboard</span>
                      )}
                    </>
                  )}
                </NavLink>
              </li>
            )}

            {/* ===================================================== */}
            {/* TEAM HUB */}
            {/* ===================================================== */}

            {role === "admin" && (
              <li>
                <NavLink
                  to="/manage-team"
                  onClick={handleMobileMenuClose}
                  className={navItemClass}
                >
                  {({ isActive }) => (
                    <>
                      <FaUsersGear
                        size={18}
                        className={iconClass(isActive)}
                      />

                      {!collapsed && (
                        <span>Team Hub</span>
                      )}
                    </>
                  )}
                </NavLink>
              </li>
            )}

            {/* ===================================================== */}
            {/* MEMBERSHIP - MOBILE */}
            {/* ===================================================== */}

            {role === "admin" && (
              <li className="md:hidden">
                <NavLink
                  to="/membership-plan-history"
                  onClick={handleMobileMenuClose}
                  className={mobileNavItemClass}
                >
                  {({ isActive }) => (
                    <>
                      <MdPayment
                        size={19}
                        className={
                          isActive
                            ? "text-[#2f66ad]"
                            : "text-[#7b8798]"
                        }
                      />

                      <span>Membership plan</span>
                    </>
                  )}
                </NavLink>
              </li>
            )}

            {/* ===================================================== */}
            {/* CONTROL PANEL */}
            {/* ===================================================== */}

            {role === "super_admin" && (
              <li>
                <NavLink
                  to="/control-panel"
                  onClick={handleMobileMenuClose}
                  className={navItemClass}
                >
                  {({ isActive }) => (
                    <>
                      <PiGameControllerDuotone
                        size={19}
                        className={iconClass(isActive)}
                      />

                      {!collapsed && (
                        <span>Control panel</span>
                      )}
                    </>
                  )}
                </NavLink>
              </li>
            )}
          </ul>

          {/* ======================================================= */}
          {/* MAIL */}
          {/* ======================================================= */}

          {!collapsed && (
            <div
              className="
                px-3
                pt-6
                pb-2
                text-[10px]
                font-bold
                tracking-[0.12em]
                uppercase
                text-[#9aa7b8]
              "
            >
              Mail
            </div>
          )}

          <ul className="space-y-1">
            {/* INBOX */}
            <li>
              <NavLink
                to="/inbox"
                onClick={handleMobileMenuClose}
                className={navItemClass}
              >
                {({ isActive }) => (
                  <>
                    <FiInbox
                      size={19}
                      className={iconClass(isActive)}
                    />

                    {!collapsed && (
                      <span>Inbox</span>
                    )}
                  </>
                )}
              </NavLink>
            </li>

            {/* DRAFTS */}
            <li>
              <NavLink
                to="/drafts"
                onClick={handleMobileMenuClose}
                className={navItemClass}
              >
                {({ isActive }) => (
                  <>
                    <FiFileText
                      size={18}
                      className={iconClass(isActive)}
                    />

                    {!collapsed && (
                      <span>Drafts</span>
                    )}
                  </>
                )}
              </NavLink>
            </li>

            {/* SENT */}
            <li>
              <NavLink
                to="/sent"
                onClick={handleMobileMenuClose}
                className={navItemClass}
              >
                {({ isActive }) => (
                  <>
                    <FiSend
                      size={18}
                      className={iconClass(isActive)}
                    />

                    {!collapsed && (
                      <span>Sent</span>
                    )}
                  </>
                )}
              </NavLink>
            </li>

            {/* STARRED */}
            <li>
              <NavLink
                to="/starred"
                onClick={handleMobileMenuClose}
                className={navItemClass}
              >
                {({ isActive }) => (
                  <>
                    <FiStar
                      size={18}
                      className={iconClass(isActive)}
                    />

                    {!collapsed && (
                      <span>Starred</span>
                    )}
                  </>
                )}
              </NavLink>
            </li>

            {/* ARCHIVE */}
            <li>
              <NavLink
                to="/archive"
                onClick={handleMobileMenuClose}
                className={navItemClass}
              >
                {({ isActive }) => (
                  <>
                    <BsArchive
                      size={17}
                      className={iconClass(isActive)}
                    />

                    {!collapsed && (
                      <span>Archive</span>
                    )}
                  </>
                )}
              </NavLink>
            </li>

            {/* TRASH */}
            <li>
              <NavLink
                to="/trash"
                onClick={handleMobileMenuClose}
                className={navItemClass}
              >
                {({ isActive }) => (
                  <>
                    <FaRegTrashCan
                      size={17}
                      className={iconClass(isActive)}
                    />

                    {!collapsed && (
                      <span>Trash</span>
                    )}
                  </>
                )}
              </NavLink>
            </li>
          </ul>

          {/* ======================================================= */}
          {/* MOBILE EXTRA */}
          {/* ======================================================= */}

          <ul className="space-y-1 mt-3 md:hidden">
            {/* HELP */}
            <li>
              <NavLink
                to="/help"
                onClick={handleMobileMenuClose}
                className={mobileNavItemClass}
              >
                {({ isActive }) => (
                  <>
                    <FiHelpCircle
                      size={18}
                      className={
                        isActive
                          ? "text-[#2f66ad]"
                          : "text-[#7b8798]"
                      }
                    />

                    <span>Help Center</span>
                  </>
                )}
              </NavLink>
            </li>
          </ul>
        </nav>

        {/* ========================================================= */}
        {/* FOOTER */}
        {/* ========================================================= */}

        <div
          className="
            flex-shrink-0
            p-3
            border-t
            border-[#edf1f6]
            bg-white
          "
        >
          {/* SETTINGS */}
          <NavLink
            to="/settings"
            onClick={handleMobileMenuClose}
            className={navItemClass}
          >
            {({ isActive }) => (
              <>
                <FiSettings
                  size={18}
                  className={iconClass(isActive)}
                />

                {!collapsed && (
                  <span>Settings</span>
                )}
              </>
            )}
          </NavLink>

          {/* LOGOUT - MOBILE */}
          <button
            onClick={handleLogout}
            className="
              md:hidden
              flex items-center gap-3
              w-full
              px-3 py-2.5
              mt-1
              rounded-xl
              text-sm
              font-medium
              text-[#536174]
              hover:bg-[#fff5f5]
              hover:text-red-500
              transition-all
            "
          >
            <LuLogOut size={18} />
            Logout
          </button>
        </div>
      </aside>
    </>
  );
}